import { LOCAL_STORAGE } from "../../constants/localStorage";

export class LanguageBanner extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const templates = Array.from(
      this.querySelectorAll("template[lang]"),
    ).filter(
      (el): el is HTMLTemplateElement => el instanceof HTMLTemplateElement,
    );
    if (!templates.length) {
      return;
    }

    const links = Array.from(
      document.head.querySelectorAll('link[rel="alternate"][hreflang]'),
    ).filter((el): el is HTMLLinkElement => el instanceof HTMLLinkElement);
    if (!links.length) {
      return;
    }

    const browserLangFull = navigator.language.toLowerCase(); // e.g., "en-us"
    const browserLangShort = browserLangFull.split("-")[0]; // "en"
    const currentUrl = window.location.href.toLowerCase();
    const currentLang = document.documentElement.lang.toLowerCase();
    const dismissed = localStorage.getItem(
      LOCAL_STORAGE.languageBannerDismissed,
    );

    // Exit: the visitor has dismissed the banner already.
    if (dismissed === browserLangFull) {
      return;
    }

    // Exit: the visitor has chosen this language in the picker.
    if (currentLang === localStorage.getItem(LOCAL_STORAGE.languageSelected)) {
      return;
    }

    // Find matching link
    const match =
      links.find((l) => l.hreflang.toLowerCase() === browserLangFull) ??
      links.find((l) => l.hreflang.toLowerCase().startsWith(browserLangShort));

    // Exit: there are no matching links; we are already in the best site.
    if (!match || currentUrl.startsWith(match.href.toLowerCase())) {
      return;
    }

    // Exit: edge case where a site may have multiple URL paths.
    if (match.hreflang.toLowerCase() === currentLang) {
      return;
    }

    // Match <template lang="xx-YY"> or <template lang="xx">
    const template =
      templates.find(
        (t) => t.getAttribute("lang")?.toLowerCase() === browserLangFull,
      ) ??
      templates.find((t) =>
        t.getAttribute("lang")?.toLowerCase().startsWith(browserLangShort),
      );

    if (!template) {
      return;
    }

    this.render(template, browserLangFull, match.href);
  }

  render(template: HTMLTemplateElement, lang: string, href: string) {
    const content = template.content.cloneNode(true);

    if (!(content instanceof DocumentFragment)) {
      return;
    }

    const anchor = content.querySelector("a");

    if (anchor) {
      anchor.setAttribute("href", href);
    }

    this.prepend(content);
    this.classList.add("--active");

    const dismiss = this.querySelector("button");
    dismiss?.addEventListener("click", () => {
      localStorage.setItem(LOCAL_STORAGE.languageBannerDismissed, lang);
      this.classList.remove("--active");
    });
  }
}

customElements.define("language-banner", LanguageBanner);
